import Swiper from 'swiper'
import { Navigation } from 'swiper/modules'

import { buildComponent } from '#components'

// ─── Constants ────────────────────────────────────────────────────────────

const DESKTOP_BREAKPOINT = 1200

const SELECTORS = {
  swiper: '.js-comparison-swiper',
  prevBtn: '.js-comparison-prev',
  nextBtn: '.js-comparison-next',
  slide: '.js-comparison-col',
  cell: '.js-comparison-cell'
} as const

// ─── Types ────────────────────────────────────────────────────────────────

interface ComparisonElement extends HTMLElement {
  _cleanup?: () => void
}

// ─── Helpers ──────────────────────────────────────────────────────────────

/**
 * Returns true when the browser natively supports CSS Subgrid.
 */
const supportsSubgrid = (): boolean =>
  typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('grid-template-rows', 'subgrid')

/**
 * Synchronizes row heights dynamically based on populated content in the DOM.
 * On desktop with native Subgrid support, inline overrides are cleared so CSS
 * can manage track sizing natively.
 */
const syncRowHeights = (root: HTMLElement): void => {
  const allCells = root.querySelectorAll<HTMLElement>(`${SELECTORS.cell}[data-row]`)

  if (!allCells.length) return

  // On desktop + subgrid: clear any previously set inline min-height and bail out.
  if (window.innerWidth >= DESKTOP_BREAKPOINT && supportsSubgrid()) {
    allCells.forEach((cell) => {
      if (cell.style.minHeight) cell.style.minHeight = ''
    })

    return
  }

  // Collect unique row identifiers dynamically from the populated DOM.
  const rowIndices = Array.from(
    new Set(
      Array.from(allCells)
        .map((cell) => cell.dataset.row)
        .filter((row): row is string => Boolean(row))
    )
  )

  // Reset min-height so we measure natural content height.
  allCells.forEach((cell) => {
    cell.style.minHeight = ''
  })

  // Equalise heights row by row.
  rowIndices.forEach((row) => {
    const rowCells = root.querySelectorAll<HTMLElement>(`${SELECTORS.cell}[data-row="${row}"]`)
    let maxHeight = 0

    rowCells.forEach((cell) => {
      const h = Math.ceil(cell.getBoundingClientRect().height) || cell.offsetHeight

      if (h > maxHeight) maxHeight = h
    })

    if (maxHeight > 0) {
      rowCells.forEach((cell) => {
        cell.style.minHeight = `${maxHeight}px`
      })
    }
  })
}

/**
 * Initialises Swiper for mobile/tablet carousel.
 * Disabled automatically at DESKTOP_BREAKPOINT via breakpoints config.
 */
const initSwiper = (root: HTMLElement, onSync: () => void): Swiper | null => {
  const swiperEl = root.querySelector<HTMLElement>(SELECTORS.swiper)

  if (!swiperEl) return null

  return new Swiper(swiperEl, {
    modules: [Navigation],
    slidesPerView: 'auto',
    spaceBetween: 0,
    grabCursor: true,
    navigation: {
      prevEl: root.querySelector<HTMLElement>(SELECTORS.prevBtn),
      nextEl: root.querySelector<HTMLElement>(SELECTORS.nextBtn)
    },
    breakpoints: {
      [DESKTOP_BREAKPOINT]: { enabled: false }
    },
    on: {
      init: onSync,
      slideChange: onSync,
      resize: onSync,
      update: onSync
    }
  })
}

// ─── Component ────────────────────────────────────────────────────────────

const ComparisonModule = buildComponent({
  name: 'comparison-module',
  onConnected: (component: ComparisonElement) => {
    let rafId: number | undefined

    /** Debounce via rAF so we never thrash layout in the same frame. */
    const handleSync = () => {
      if (rafId !== undefined) window.cancelAnimationFrame(rafId)
      rafId = window.requestAnimationFrame(() => syncRowHeights(component))
    }

    // 1. Initial sync
    handleSync()

    // 2. Re-sync after custom web-fonts finish loading (prevents reflow desync)
    document.fonts?.ready.then(handleSync)

    // 3. Observe container size changes (covers window resize + CSS container queries)
    const resizeObserver = new ResizeObserver(handleSync)

    resizeObserver.observe(component)

    // 4. Swiper
    const swiperInstance = initSwiper(component, handleSync)

    // 5. Shopify Customizer events
    const handleBlockSelect = (event: Event) => {
      const blockId = (event as CustomEvent<{ blockId: string }>).detail?.blockId

      if (!blockId || !swiperInstance) return

      const slides = Array.from(component.querySelectorAll<HTMLElement>(SELECTORS.slide))
      const targetIndex = slides.findIndex((slide) => slide.dataset.blockId === blockId)

      if (targetIndex !== -1) swiperInstance.slideTo(targetIndex)

      handleSync()
    }

    const handleSectionLoad = () => handleSync()

    document.addEventListener('shopify:block:select', handleBlockSelect)
    document.addEventListener('shopify:section:load', handleSectionLoad)

    // 6. Cleanup
    component._cleanup = () => {
      if (rafId !== undefined) window.cancelAnimationFrame(rafId)
      resizeObserver.disconnect()
      document.removeEventListener('shopify:block:select', handleBlockSelect)
      document.removeEventListener('shopify:section:load', handleSectionLoad)
      swiperInstance?.destroy(true, true)
    }
  },

  onDisconnected: (component: ComparisonElement) => {
    component._cleanup?.()
    component._cleanup = undefined
  }
})

// ─── Global type registration ─────────────────────────────────────────────

declare global {
  interface HTMLElementTagNameMap {
    'comparison-module': typeof ComparisonModule
  }
}
